import { Popover } from "@mui/material";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { FramerTooltip } from "@ryde/controls";
import { useEmitterListener } from "@ryde/redux";
import {
  currencySignSelector,
  formatPrice,
  GlobalAppEventEnum,
  IRideDto,
  Log,
  TOOLTIP_DELAY_BUTTONS,
  useDrawers,
} from "@ryde/shared";

import { ButtonHeader } from "../../../../right";
import { RideHeaderPriceSupplierDropdown } from "./ride-header-price-supplier-dropdown";

interface IRideHeaderPriceSupplier {
  rideDto?: Partial<IRideDto>;
}

export const RideHeaderPriceSupplier: FC<IRideHeaderPriceSupplier> = ({
  rideDto,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { openEditFormDrawer } = useDrawers();
  const currencySign = useRecoilValue(currencySignSelector);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useEvent((): void => {
    setAnchorEl(null);
  });

  const handleSupplierPriceRateDrawerOpen = useEvent(
    async (id: number): Promise<void> => {
      setAnchorEl(null);
      openEditFormDrawer({
        entity: "supplierPriceRate",
        id,
      });
      Log.debug(`Form form for Supplier Price Rate with id ${id} closed`);
    },
  );

  const open = !!anchorEl;
  const supplierPriceText = `${currencySign}${formatPrice({ value: rideDto?.supplierPrice ?? 0 })}`;

  useEmitterListener({
    event: GlobalAppEventEnum.START_DRAG,
    handler: handleClose,
    isEnabled: open,
  });

  return (
    rideDto && (
      <>
        {/* The button shows the price alone (RYDE-8064); what it opens is carried by the tooltip and the
            accessible name, so the header stays readable without losing the affordance. */}
        <FramerTooltip
          content={t("general.BREAKDOWN")}
          mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
        >
          <ButtonHeader
            onClick={handleClick}
            $active={open}
            aria-label={`${t("general.BREAKDOWN")} ${supplierPriceText}`}
          >
            <SVG_MUI.BriefcaseOutline2SVG data-e2e="ride-svg-supplier-price" />
            <span data-e2e="ride-supplier-price">{supplierPriceText}</span>
          </ButtonHeader>
        </FramerTooltip>
        {open && (
          <Popover
            id={"ride-supplier-price-popover"}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 40,
              horizontal: "left",
            }}
          >
            <RideHeaderPriceSupplierDropdown
              handleSupplierPriceRateDrawerOpen={
                handleSupplierPriceRateDrawerOpen
              }
              rideDto={rideDto}
            />
          </Popover>
        )}
      </>
    )
  );
};
